import { createMemo, createResource, For, Show } from "solid-js";
import { useNavigate } from "@solidjs/router";
import { api } from "../services.ts";
import { locale, t } from "../i18n.ts";
import { formatNumber, formatShare } from "../format.ts";
import { siteTree } from "../context.ts";
import { freshness } from "../overview.ts";
import { layoutSupply } from "../supply.ts";
import { mediumColor } from "./Media.tsx";
import { ErrorBox } from "./ErrorBox.tsx";
import { Skeleton } from "./ui.tsx";

const COL = 230;
const BOX_W = 190;
const BOX_H = 46;
const ROW = 62;
const PAD = 10;

/**
 * The supply topology of the scope (prototype "Topologia"): grid connections on the left, what each feeds to the
 * right, power now on every meter, the load of a connection against its capacity, and dashed lines to the backup a
 * transfer switch moves a section to. Built from the supply set on points, otherwise from the place tree.
 */
export function SupplyTopology(props: { nodeId: string }) {
  const navigate = useNavigate();
  const [links] = createResource(
    () => props.nodeId,
    (id) => api.sites.supply(id),
  );
  const [now] = createResource(
    () => props.nodeId,
    (id) => api.sites.now(id),
  );
  const points = () =>
    new Map((siteTree()?.points ?? []).map((p) => [p.id, p]));
  const power = () => new Map((now()?.points ?? []).map((p) => [p.pointId, p]));
  const layout = createMemo(() =>
    layoutSupply(links() ?? [], (a, b) => {
      const pa = points().get(a);
      const pb = points().get(b);
      return (
        (pa?.role === "main" ? 0 : 1) - (pb?.role === "main" ? 0 : 1) ||
        (pa?.name ?? "").localeCompare(pb?.name ?? "")
      );
    }),
  );
  const at = createMemo(() => new Map(layout().placed.map((p) => [p.id, p])));
  const x = (depth: number) => PAD + depth * COL;
  const y = (row: number) => PAD + row * ROW;
  const W = () => x(layout().depth) + BOX_W + PAD;
  const H = () => y(Math.max(1, layout().rows)) + PAD - (ROW - BOX_H);

  const edge = (from: string, to: string) => {
    const a = at().get(from)!;
    const b = at().get(to)!;
    const x1 = x(a.depth) + BOX_W;
    const y1 = y(a.row) + BOX_H / 2;
    const x2 = x(b.depth);
    const y2 = y(b.row) + BOX_H / 2;
    const mid = x2 > x1 ? (x1 + x2) / 2 : x1 + 14;
    return `M${x1} ${y1} H${mid} V${y2} H${x2}`;
  };

  return (
    <Show when={!links.error} fallback={<ErrorBox error={links.error} />}>
      <Show
        when={links()}
        fallback={
          <div class="card-b">
            <Skeleton lines={6} />
          </div>
        }
      >
        <Show
          when={layout().placed.length > 0}
          fallback={<div class="card-b hint">{t("supply.empty")}</div>}
        >
          <div class="card-b scrollx">
            <svg
              viewBox={`0 0 ${W()} ${H()}`}
              style={{
                width: `${W()}px`,
                "max-width": "none",
                height: "auto",
                display: "block",
                "font-family": "var(--font-ui)",
                "font-size": "12px",
              }}
              role="img"
              aria-label={t("supply.title")}
            >
              <For each={layout().edges.filter((e) => !e.backup)}>
                {(e) => (
                  <path
                    d={edge(e.from, e.to)}
                    fill="none"
                    stroke="var(--line-2)"
                    stroke-width="2"
                  />
                )}
              </For>
              <For each={layout().edges.filter((e) => e.backup)}>
                {(e) => (
                  <g>
                    <path
                      d={edge(e.from, e.to)}
                      fill="none"
                      stroke="var(--warn)"
                      stroke-width="1.5"
                      stroke-dasharray="5 4"
                    />
                    <title>{t("supply.backupLine")}</title>
                  </g>
                )}
              </For>
              <For each={layout().placed}>
                {(pl) => {
                  const p = () => points().get(pl.id);
                  const pw = () => power().get(pl.id);
                  const state = () => freshness(pw()?.at ?? null, Date.now());
                  const cap = () =>
                    p()?.role === "main" ? (p()?.capacityKw ?? null) : null;
                  const use = () =>
                    cap() !== null && pw()?.power != null
                      ? Math.max(0, pw()!.power!) / cap()!
                      : null;
                  return (
                    <g
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate(`/assets?point=${encodeURIComponent(pl.id)}`)
                      }
                    >
                      <rect
                        x={x(pl.depth)}
                        y={y(pl.row)}
                        width={BOX_W}
                        height={BOX_H}
                        rx="6"
                        fill="var(--surface)"
                        stroke={
                          p()?.role === "main" ? "var(--ink)" : "var(--line-2)"
                        }
                        stroke-width={p()?.role === "main" ? 1.5 : 1}
                      />
                      <rect
                        x={x(pl.depth)}
                        y={y(pl.row)}
                        width="4"
                        height={BOX_H}
                        rx="2"
                        fill={mediumColor(p()?.medium ?? "el")}
                      />
                      <text
                        x={x(pl.depth) + 12}
                        y={y(pl.row) + 18}
                        fill="var(--ink)"
                        style={{ "font-weight": 500 }}
                      >
                        {(p()?.name ?? "").length > 24
                          ? `${p()!.name.slice(0, 23)}…`
                          : p()?.name}
                      </text>
                      <text
                        x={x(pl.depth) + 12}
                        y={y(pl.row) + 35}
                        fill={state() === "ok" ? "var(--muted)" : "var(--warn)"}
                        style={{
                          "font-family": "var(--font-mono)",
                          "font-size": "11px",
                        }}
                      >
                        {pw()?.power == null
                          ? state() === "none"
                            ? t("overview.noReading")
                            : "—"
                          : `${formatNumber(pw()!.power, locale(), 2)} kW`}
                        {cap() !== null
                          ? ` / ${formatNumber(cap(), locale(), 0)} kW`
                          : ""}
                        {p()?.role === "main"
                          ? ""
                          : ` · ${t(`role.point.${p()?.role}`)}`}
                      </text>
                      <Show when={use() !== null}>
                        <rect
                          x={x(pl.depth) + 12}
                          y={y(pl.row) + 40}
                          width={BOX_W - 24}
                          height="3"
                          rx="1.5"
                          fill="var(--sunken)"
                        />
                        <rect
                          x={x(pl.depth) + 12}
                          y={y(pl.row) + 40}
                          width={(BOX_W - 24) * Math.min(1, use()!)}
                          height="3"
                          rx="1.5"
                          fill={
                            use()! > 0.9
                              ? "var(--crit)"
                              : use()! > 0.7
                                ? "var(--warn)"
                                : "var(--ok)"
                          }
                        >
                          <title>
                            {t("supply.capacityUse", {
                              share: formatShare(use(), locale()),
                            })}
                          </title>
                        </rect>
                      </Show>
                    </g>
                  );
                }}
              </For>
            </svg>
          </div>
          <div class="legend">
            <span>
              <i class="sw" style={{ "border-top-color": "var(--line-2)" }} />
              {t("supply.normal")}
            </span>
            <span>
              <i
                class="sw dash"
                style={{ "border-top-color": "var(--warn)" }}
              />
              {t("supply.backupLine")}
            </span>
            <span>{t("supply.legendCapacity")}</span>
          </div>
        </Show>
        <Show when={layout().unassigned.length > 0}>
          <div
            class="note"
            style={{
              "border-top": "1px solid var(--line)",
              "flex-wrap": "wrap",
            }}
          >
            <span class="chip warn">{t("supply.unassigned")}</span>
            <For each={layout().unassigned}>
              {(id) => (
                <button
                  type="button"
                  class="btn ghost sm"
                  onClick={() =>
                    navigate(`/assets?point=${encodeURIComponent(id)}`)
                  }
                >
                  {points().get(id)?.name}
                </button>
              )}
            </For>
            <span class="hint">{t("supply.unassignedHint")}</span>
          </div>
        </Show>
        <div
          class="note"
          style={{
            "border-top": "1px solid var(--line)",
            "border-bottom": "0",
          }}
        >
          {t("supply.note")}
        </div>
      </Show>
    </Show>
  );
}
